import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import BlockedSubscription from 'components/molecules/blocked_subscription';
import { ApplicationState } from 'store';
import { ProfileState } from 'store/modules/profile_store/types';
import createTrack from 'services/actions/tracker_actions';
import HelpVideoModal from 'components/molecules/help_video_modal';
import AiManagementTabs, { TabKey } from 'components/molecules/ai_management_tabs';
import AiManagementViewSwitcher, {
  ViewMode,
} from 'components/molecules/ai_management_view_switcher';
import AiManagementKanban from 'components/organisms/ai_management_kanban';
import AiManagementList from 'components/organisms/ai_management_list';
import AiManagementCalendar from 'components/organisms/ai_management_calendar';
import AiManagementTasksKanban from 'components/organisms/ai_management_tasks_kanban';
import AiManagementTasksList from 'components/organisms/ai_management_tasks_list';
import AiManagementTasksCalendar from 'components/organisms/ai_management_tasks_calendar';
import AiManagementUpdates from 'components/organisms/ai_management_updates';
import AiManagementArchivedList from 'components/organisms/ai_management_archived_list';
import { getUnreadUpdatesCount } from 'services/actions/ai_management_updates';
import { UNREAD_POLL_INTERVAL_MS } from 'services/ai_management_updates';
import { Container } from './styles';

const AIManagement = () => {
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const [modalVideoOpened, setModalVideoOpened] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TabKey>('minhas_licitacoes');
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [unreadUpdates, setUnreadUpdates] = useState<number>(0);

  useEffect(() => {
    document.title = 'Gestão IA - ConLicitação';
  }, []);

  const refreshUnreadUpdates = useCallback(() => {
    getUnreadUpdatesCount()
      .then((response) => {
        if (response?.status === 200) setUnreadUpdates(response.data?.unread_count ?? 0);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshUnreadUpdates();

    const interval = window.setInterval(() => {
      if (!document.hidden) refreshUnreadUpdates();
    }, UNREAD_POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (!document.hidden) refreshUnreadUpdates();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshUnreadUpdates]);

  return (
    <Container className="page-container">
      {profile.paralyzed ? (
        <BlockedSubscription paralyzed={profile.paralyzed} />
      ) : (
        <div className="container-fluid d-flex flex-column flex-grow-1">
          <div className="row">
            <div className="col-md-12 mt-3">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="d-md-flex d-block align-items-center">
                    <h4 className="mb-1 mr-3">Gestão IA</h4>
                    <button
                      type="button"
                      className="btn btn-link p-0 btn-sm d-flex align-items-center"
                      onClick={() => {
                        setModalVideoOpened(true);
                        createTrack('Clicou no botão video tutorial', {
                          fonte: 'Gestão IA',
                        });
                      }}
                    >
                      <i className="material-icons mr-1">videocam</i>
                      Veja como funciona
                    </button>
                  </div>
                </div>
                <button type="button" className="btn btn-secondary">
                  Configurações
                </button>
              </div>
              <AiManagementTabs
                activeTab={activeTab}
                onChange={(tab) => {
                  setActiveTab(tab);
                  if (tab !== 'atualizacoes') refreshUnreadUpdates();
                }}
                badges={{ atualizacoes: unreadUpdates }}
              />
            </div>
          </div>

          <div className="row flex-grow-1">
            <div className="col-12 bg-white">
              <div className="tab-content">
                {activeTab === 'minhas_licitacoes' && (
                  <div>
                    <AiManagementViewSwitcher viewMode={viewMode} onChange={setViewMode} />

                    {viewMode === 'kanban' && <AiManagementKanban />}
                    {viewMode === 'lista' && <AiManagementList />}
                    {viewMode === 'calendario' && <AiManagementCalendar />}
                  </div>
                )}
                {activeTab === 'tarefas' && (
                  <div>
                    <AiManagementViewSwitcher viewMode={viewMode} onChange={setViewMode} />

                    {viewMode === 'kanban' && <AiManagementTasksKanban />}
                    {viewMode === 'lista' && <AiManagementTasksList />}
                    {viewMode === 'calendario' && <AiManagementTasksCalendar />}
                  </div>
                )}
                {activeTab === 'arquivadas' && (
                  <div className="pt-3">
                    <AiManagementArchivedList />
                  </div>
                )}
                {activeTab === 'atualizacoes' && (
                  <AiManagementUpdates onUnreadCountChange={setUnreadUpdates} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <HelpVideoModal
        modalVideoOpened={modalVideoOpened}
        setModalVideoOpened={setModalVideoOpened}
        urlVideo="https://player.vimeo.com/video/1148631963"
      />
    </Container>
  );
};
export { AIManagement };
